import React, { useRef, useState } from 'react';
import { Button, Checkbox, Menu, Popover, Chip } from '@milanote/roma';
import { cx } from '@milanote/roma/utils';
import { DotsHorizontal } from '@untitledui/icons';
import { GridListItem } from 'react-aria-components';
import { useDispatch, useSelector } from 'react-redux';

import { Label } from '../../../../../../../common/labels/labelTypes';
import { updateLabel } from '../../../../../../../common/labels/labelActions';
import LabelListItemActionsPopover from './LabelListItemActionsPopover';
import { LABEL_NAME_MAX_LENGTH } from '../../../../../../../common/labels/labelConstants';
import { getLabelColorHex, getLabelOutlinePropertiesForMenu } from '../../../../../../element/labels/labelUtils';
import { getThemeIsDarkMode } from '../../../../../../user/account/accountModal/interface/themeSettings/themeSelector';

const styles = {
    sm: {
        root: '',
        itemSize: 'sm',
        checkboxWrapper: 'p-0.5',
        checkboxSize: 'sm',
        labelSize: 'md',
        buttonSize: 'sm',
    },
    lg: {
        root: '',
        itemSize: 'lg',
        checkboxWrapper: '',
        checkboxSize: 'md',
        labelSize: 'lg',
        buttonSize: 'md',
    },
} as const;

type Props = {
    label: Label;
    onEditingChange: (labelId: string, nextIsEditing: boolean) => void;
    onEditClick?: (label: Label) => void;
    isSheet?: boolean;
    isSelected?: boolean;
    isEditing: boolean;
    size?: 'sm' | 'lg';
};

const LabelListItem: React.FC<Props> = ({
    label,
    onEditingChange,
    onEditClick,
    isSheet = false,
    isSelected = false,
    isEditing = false,
    size = 'sm',
}) => {
    const dispatch = useDispatch();
    const [isActionsOpen, setIsActionsOpen] = useState<boolean>(false);
    const actionsTriggerRef = useRef<HTMLButtonElement>(null);
    const isDarkModeTheme = useSelector(getThemeIsDarkMode);
    const { _id: labelId, name } = label;

    const sizeStyles = styles[size];
    const labelColorHex = getLabelColorHex(label);

    const handleEditingChange = (nextIsEditing: boolean) => {
        onEditingChange(labelId, nextIsEditing);
    };

    const handleCommitRename = (nextName: string) => {
        dispatch(updateLabel(labelId, { name: nextName }));
    };

    const handleRename = () => {
        setIsActionsOpen(false);
        handleEditingChange(true);
    };

    const opensEditSheet = isSheet && !!onEditClick;

    const handleActionClick = () => {
        if (opensEditSheet) {
            onEditClick(label);
            return;
        }

        setIsActionsOpen((currentIsOpen) => !currentIsOpen);
    };

    const { hasSimilarColorToBackground, outlineColor } = getLabelOutlinePropertiesForMenu(label, isDarkModeTheme);

    return (
        <GridListItem
            id={labelId}
            textValue={name}
            className={cx('LabelListItem', 'w-full py-0.5 outline-hidden cursor-pointer', sizeStyles.root)}
        >
            {(state) => (
                <Menu.Base.Item
                    isSelected={isSelected}
                    isDisabled={state.isDisabled}
                    isFocused={state.isFocused}
                    isFocusVisible={state.isFocusVisible}
                    isHovered={state.isHovered}
                    className="mx-0.5 pl-3 pr-0"
                    size={sizeStyles.itemSize}
                >
                    <div className="flex min-w-0 w-full items-center gap-md">
                        <div className={cx(sizeStyles.checkboxWrapper)}>
                            <Checkbox.Base
                                size={sizeStyles.checkboxSize}
                                variant="primary"
                                isSelected={isSelected}
                                isDisabled={state.isDisabled}
                            />
                        </div>

                        <div
                            className={cx(
                                'min-w-0 -my-1',
                                hasSimilarColorToBackground && `outline ${outlineColor} rounded-xs`,
                            )}
                        >
                            <Chip
                                editable
                                className="inline-flex w-auto max-w-full"
                                dataChipId={labelId}
                                value={name}
                                color={labelColorHex}
                                size={sizeStyles.labelSize}
                                isEditing={isEditing}
                                onEditingChange={handleEditingChange}
                                onCommit={handleCommitRename}
                                maxLength={LABEL_NAME_MAX_LENGTH}
                            />
                        </div>

                        <Button
                            ref={actionsTriggerRef}
                            color="tertiary"
                            size={sizeStyles.buttonSize}
                            aria-label={`Open actions for ${label.name}`}
                            className={cx(
                                'ml-auto shrink-0',
                                // expand padding out to touch the edge of the label button
                                'data-icon-only:-my-2',
                            )}
                            onClick={handleActionClick}
                            iconLeading={
                                <DotsHorizontal
                                    className={cx('size-4 text-quaternary', isActionsOpen && 'text-fg-primary')}
                                />
                            }
                        />

                        {!opensEditSheet && (
                            <Popover
                                triggerRef={actionsTriggerRef}
                                isOpen={isActionsOpen}
                                onOpenChange={setIsActionsOpen}
                                placement="end top"
                                offset={4}
                                crossOffset={-6}
                                className="min-w-52"
                            >
                                <LabelListItemActionsPopover
                                    label={label}
                                    onRename={handleRename}
                                    onClose={() => setIsActionsOpen(false)}
                                />
                            </Popover>
                        )}
                    </div>
                </Menu.Base.Item>
            )}
        </GridListItem>
    );
};

export default LabelListItem;
